import { Package, X } from 'lucide-react'
import { ProcessLinkPicker } from './ProcessLinkPicker'
import {
  MAX_RELATED_PROCESSES,
  type IssueProcessLink,
  type ProcessAnswer,
} from '../../hooks/useIssueProcessLink'

interface ProcessLinkFieldProps {
  link: IssueProcessLink
  /** Processo da página atual — número e empresa já resolvidos (ou `#id`
   * enquanto carrega). */
  contextProcess: { id: number; label: string; company: string | null } | null
}

const QUESTION_ID = 'new-issue-process-question'

const OPTIONS: Array<{ value: Exclude<ProcessAnswer, null>; label: string }> = [
  { value: 'yes', label: 'Sim' },
  { value: 'no', label: 'Não' },
]

/**
 * Pergunta obrigatória do novo chamado — "tem relação com um ou mais
 * processos?" — e, com "sim", a lista de processos vinculados (ao menos um)
 * com a busca para adicionar mais. Estado em `useIssueProcessLink`.
 */
export function ProcessLinkField({ link, contextProcess }: ProcessLinkFieldProps) {
  if (!link.asks) return null
  const empty = link.answer === 'yes' && link.linkedIds.length === 0

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 dark:border-blue-900/60 dark:bg-blue-900/15">
      <p id={QUESTION_ID} className="text-xs font-semibold text-body">
        Esse atendimento tem relação com um ou mais processos específicos?{' '}
        <span className="text-red-600 dark:text-red-400" aria-hidden>
          *
        </span>
      </p>
      <div
        role="radiogroup"
        aria-labelledby={QUESTION_ID}
        aria-required="true"
        className="mt-2 flex gap-2"
      >
        {OPTIONS.map((o) => (
          <label key={o.value} className="cursor-pointer">
            <input
              type="radio"
              name="new-issue-has-process"
              value={o.value}
              checked={link.answer === o.value}
              disabled={link.locked}
              onChange={() => link.setAnswer(o.value)}
              className="peer sr-only"
            />
            <span className="inline-flex h-8 min-w-[4.5rem] items-center justify-center rounded-md border border-border-subtle bg-surface px-3 text-xs font-semibold text-body transition hover:bg-surface-muted peer-checked:border-blue-600 peer-checked:bg-blue-600 peer-checked:text-white peer-focus-visible:ring-2 peer-focus-visible:ring-blue-300 peer-disabled:cursor-default peer-[:disabled:not(:checked)]:opacity-50">
              {o.label}
            </span>
          </label>
        ))}
      </div>
      {link.answer === null && (
        <p className="mt-1.5 text-[11px] text-muted">
          Responda para enviar — com "Sim", vincule os processos e a equipe já
          começa por eles.
        </p>
      )}

      {link.answer === 'yes' && (
        <div className="mt-3 space-y-2">
          {link.linkedIds.length > 0 && (
            <ul className="flex flex-wrap gap-1.5" aria-label="Processos vinculados">
              {link.withContext && contextProcess && (
                <ProcessChip
                  label={contextProcess.label}
                  company={contextProcess.company}
                  onRemove={link.locked ? undefined : link.removeContext}
                />
              )}
              {link.picked.map((p) => (
                <ProcessChip
                  key={p.id}
                  label={p.process_number ?? `#${p.id}`}
                  company={p.company}
                  onRemove={() => link.remove(p.id)}
                />
              ))}
            </ul>
          )}
          {link.locked && (
            <p className="text-[11px] text-muted">
              Aberto pela aba HUB do processo — o vínculo com ele é automático.
            </p>
          )}
          {link.canAddMore && (
            <ProcessLinkPicker excludeIds={link.linkedIds} onSelect={link.add} />
          )}
          {link.linkedIds.length >= MAX_RELATED_PROCESSES && (
            <p className="text-[11px] text-muted">
              Limite de {MAX_RELATED_PROCESSES} processos por chamado.
            </p>
          )}
          <p
            className={`text-[11px] ${empty ? 'font-medium text-red-700 dark:text-red-400' : 'text-subtle'}`}
          >
            {empty
              ? 'Vincule ao menos um processo para enviar.'
              : 'O chamado também aparece na aba "HUB" de cada processo vinculado.'}
          </p>
        </div>
      )}
    </div>
  )
}

function ProcessChip({
  label,
  company,
  onRemove,
}: {
  label: string
  company: string | null
  onRemove?: () => void
}) {
  return (
    <li className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-border-subtle bg-surface py-1 pl-2 pr-1 text-xs text-body">
      <Package className="h-3.5 w-3.5 shrink-0 text-subtle" aria-hidden />
      <span className="min-w-0 truncate">
        <strong className="font-mono">{label}</strong>
        {company && <span className="text-muted"> — {company}</span>}
      </span>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remover o processo ${label}`}
          className="shrink-0 rounded p-0.5 text-subtle hover:bg-surface-muted hover:text-body"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : (
        <span className="w-1" aria-hidden />
      )}
    </li>
  )
}
